import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import useReveal from "../shared/useReveal";
import motilal from "../../assets/brokers/motilal-oswal.png";
import njWealth from "../../assets/brokers/nj-wealth.png";
import zerodha from "../../assets/brokers/zerodha-kite.svg";
import paytmMoney from "../../assets/brokers/paytm-money.png";
import dhan from "../../assets/brokers/dhan.svg";
import angelOne from "../../assets/brokers/angel-one.png";
import "./AlgoDesk.css";
import "./HomeSections.css";

const WA_BROKER_URL =
  "https://wa.me/919784862667?text=" +
  encodeURIComponent(
    "Hi Poonji Mitra, I'd like to talk about broker / white-label algo integration."
  );

const FEATS = [
  {
    icon: "🤖",
    title: "Rule-Based Algo Strategies",
    desc: "Back-tested systems with hard stop-losses, exposure caps and drawdown limits — emotion removed, risk defined.",
  },
  {
    icon: "🧠",
    title: "AI-Assisted Research",
    desc: "Machine screening across 4,000+ listed stocks — fundamentals, price action, ownership and momentum scored daily.",
  },
  {
    icon: "🎙️",
    title: "Earnings-Call Intelligence",
    desc: "NLP reads management commentary from concalls & annual reports to flag tone shifts and guidance changes early.",
  },
  {
    icon: "🧑‍💼",
    title: "Human-in-the-Loop",
    desc: "Every AI signal is reviewed by our CFA-led desk before it touches a portfolio. Machines suggest; experts decide.",
  },
  {
    icon: "🤝",
    title: "For Brokers — White Label",
    desc: "Algo infrastructure, strategy marketplace, API integration and backend support under your own brand.",
  },
];

const SIGNALS = [
  { name: "INFY · Momentum+", tag: "BUY", warn: false },
  { name: "RELIANCE · Mean-rev", tag: "WATCH", warn: true },
  { name: "HDFCBANK · Trend", tag: "HOLD", warn: false },
  { name: "TCS · Earnings NLP", tag: "POSITIVE", warn: false },
];

const BROKERS = [
  {
    name: "Motilal Oswal",
    logo: motilal,
    blurb: "Full-service research & advisory desk",
  },
  {
    name: "NJ Wealth",
    logo: njWealth,
    blurb: "Mutual fund & wealth distribution network",
  },
  {
    name: "Zerodha Kite",
    logo: zerodha,
    blurb: "Discount brokerage & trading platform",
  },
  {
    name: "Paytm Money",
    logo: paytmMoney,
    blurb: "Investing & demat for retail investors",
  },
  {
    name: "Dhan",
    logo: dhan,
    blurb: "Modern trading with advanced tools",
  },
  {
    name: "Angel One",
    logo: angelOne,
    blurb: "SmartAPI-ready full-service broker",
  },
];

export default function AlgoDesk() {
  const ref = useReveal();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <section className="section home-aidesk" id="algo-ai">
      <div className="container" ref={ref}>
        <div className="home-section-head section-head pm-reveal">
          <span className="kicker">Algo Trading × AI Research</span>
          <h2>
            Machines execute. Intelligence decides.
            <br />
            Experts stay accountable.
          </h2>
          <p className="sub">
            Poonji&apos;s quant desk pairs rule-based algo execution with
            AI-assisted research — so decisions are driven by data, discipline
            and a CFA who signs off on every strategy.
          </p>
        </div>

        <div className="home-ai-grid">
          <div className="pm-reveal">
            <div className="home-ai-feats">
              {FEATS.map((f) => (
                <div className="home-ai-feat" key={f.title}>
                  <div className="fi">{f.icon}</div>
                  <div>
                    <b>{f.title}</b>
                    <span>{f.desc}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="home-aidesk-actions">
              <Link to="/algo-trading" className="btn btn-primary">
                Explore the Quant Desk
              </Link>
              <button
                type="button"
                className="btn home-aidesk-ghost"
                onClick={() => setOpen(true)}
              >
                Broker Solutions
              </button>
            </div>
          </div>

          <div className="home-terminal pm-reveal" aria-label="Sample strategy terminal">
            <div className="home-term-head">
              <span className="home-term-dot" style={{ background: "#E5484D" }} />
              <span className="home-term-dot" style={{ background: "#F6D77C" }} />
              <span className="home-term-dot" style={{ background: "#3FD68F" }} />
              <span style={{ marginLeft: 8 }}>
                POONJI QUANT DESK — STRATEGY: TREND-RIDER v3 (BACKTEST)
              </span>
            </div>
            <div className="home-term-body">
              <svg
                className="home-eq-curve"
                viewBox="0 0 400 170"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <defs>
                  <linearGradient id="eqFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#3FD68F" stopOpacity="0.35" />
                    <stop offset="1" stopColor="#3FD68F" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path
                  d="M0,140 C40,130 70,120 100,110 S160,70 200,75 S280,40 320,35 L400,20 L400,170 L0,170 Z"
                  fill="url(#eqFill)"
                />
                <path
                  d="M0,140 C40,130 70,120 100,110 S160,70 200,75 S280,40 320,35 L400,20"
                  fill="none"
                  stroke="#3FD68F"
                  strokeWidth="2.5"
                />
              </svg>

              <div className="home-term-metrics">
                <div className="home-tmetric">
                  <b>21.4%</b>
                  <span>CAGR</span>
                </div>
                <div className="home-tmetric gold">
                  <b>-8.6%</b>
                  <span>Max DD</span>
                </div>
                <div className="home-tmetric">
                  <b>1.72</b>
                  <span>Sharpe</span>
                </div>
                <div className="home-tmetric">
                  <b>63%</b>
                  <span>Win Rate</span>
                </div>
              </div>

              <div className="home-signal-head">
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: "#3FD68F",
                    display: "inline-block",
                  }}
                />
                AI RESEARCH FEED — SAMPLE SIGNALS
              </div>
              {SIGNALS.map((s) => (
                <div className={`home-sig${s.warn ? " warn" : ""}`} key={s.name}>
                  <b>{s.name}</b>
                  <span className="tag">{s.tag}</span>
                </div>
              ))}
              <p className="home-term-note">
                Illustrative backtest & sample signals for demonstration — not
                live recommendations. Algo trading involves market risk; past
                performance never guarantees future returns.
              </p>
            </div>
          </div>
        </div>
      </div>

      {open && (
        <div
          className="home-broker-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="broker-modal-title"
          onClick={() => setOpen(false)}
        >
          <div
            className="home-broker-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="home-broker-panel-head">
              <div>
                <span className="kicker">White-label partners</span>
                <h3 id="broker-modal-title">Broker Solutions</h3>
                <p>
                  Connect your brokerage stack — algo infrastructure, strategy
                  marketplace and API support under your brand.
                </p>
              </div>
              <button
                type="button"
                className="home-broker-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="home-broker-grid">
              {BROKERS.map((b) => (
                <Link
                  key={b.name}
                  className="home-broker-card"
                  to="/contact"
                  onClick={() => setOpen(false)}
                >
                  <img src={b.logo} alt="" width={48} height={48} />
                  <strong>{b.name}</strong>
                  <span>{b.blurb}</span>
                </Link>
              ))}
            </div>

            <div className="home-broker-panel-foot">
              <a
                href={WA_BROKER_URL}
                className="btn btn-primary"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
              >
                Talk to us about integration
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
